import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Clock, X, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useSlaAlerts } from '@/hooks/useSlaAlerts';

export default function SlaAlert() {
  const { slaAlerts, loading } = useSlaAlerts();
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  // Verificar se o alerta foi fechado recentemente
  useEffect(() => {
    const dismissedTime = localStorage.getItem('slaAlertDismissed');
    if (dismissedTime) {
      const dismissedAt = parseInt(dismissedTime);
      const now = Date.now();
      const thirtyMinutes = 30 * 60 * 1000; // 30 minutos em millisegundos
      
      if (now - dismissedAt < thirtyMinutes) {
        setDismissed(true);
      } else {
        // Limpar o timestamp se já passou de 30 minutos
        localStorage.removeItem('slaAlertDismissed');
        setDismissed(false);
      }
    }
  }, []);

  // Se não há alertas ou usuário não é admin/dev, não mostra nada
  if (!slaAlerts || slaAlerts.total === 0) {
    return null;
  }

  // Se foi fechado, não mostrar nada
  if (dismissed) {
    return null;
  }

  return (
    <div className={`fixed top-4 left-4 right-4 z-50 max-w-4xl mx-auto transition-all duration-300 ${
      dismissed ? 'opacity-0 -translate-y-12 scale-90' : 'opacity-100 translate-y-0 scale-100'
    }`}>
      <div className="bg-gradient-to-r from-red-500 to-orange-500 dark:from-red-600 dark:to-orange-600 rounded-xl shadow-2xl border border-red-200 dark:border-red-700 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 text-white">
          <div className="flex items-center space-x-3">
            <div className="animate-pulse">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-bold text-lg">
                ⚠️ SLA ESTOURADO - {slaAlerts.total} ticket{slaAlerts.total > 1 ? 's' : ''}
              </h3>
              <p className="text-red-100 dark:text-red-200 text-sm">
                Ticket{slaAlerts.total > 1 ? 's' : ''} com prazo de atendimento vencido
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                setExpanded(!expanded);
                // Se estiver expandindo, resetar o estado de fechado
                if (!expanded) {
                  setDismissed(false);
                  localStorage.removeItem('slaAlertDismissed');
                }
              }}
              className="p-2 hover:bg-red-600/50 dark:hover:bg-red-700/50 rounded-lg transition-all duration-150 hover:scale-105"
            >
              {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            
            <button
              onClick={() => {
                setDismissed(true);
                // Salvar timestamp de quando foi fechado
                localStorage.setItem('slaAlertDismissed', Date.now().toString());
              }}
              className="p-2 hover:bg-red-600/50 dark:hover:bg-red-700/50 rounded-lg transition-all duration-150 hover:scale-105"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Expandable Content */}
        {expanded && (
          <div className={`bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm transition-all duration-300 ${
            expanded ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
          }`}>
            <div className="p-4 max-h-64 overflow-y-auto">
              <div className="space-y-3">
                {slaAlerts.tickets.map((ticket, index) => (
                  <div
                    key={ticket.id}
                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                    className={`flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/30 rounded-lg border border-red-200 dark:border-red-700 transition-all duration-200 transform cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/50 hover:border-red-300 dark:hover:border-red-600 ${
                      expanded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-5'
                    }`}
                    style={{ transitionDelay: `${index * 50}ms` }}
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          #{ticket.id} - {ticket.assunto}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                          ticket.prioridade === 'Alta' ? 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300' :
                          ticket.prioridade === 'Média' ? 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300' :
                          'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300'
                        }`}>
                          {ticket.prioridade}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center space-x-1">
                          <Clock className="w-4 h-4 text-red-500 dark:text-red-400" />
                          <span className="text-red-600 dark:text-red-400 font-medium">
                            {ticket.tempo_atras} atrás
                          </span>
                        </div>
                        <span className="text-gray-500 dark:text-gray-400">
                          SLA: {ticket.sla_valor} {ticket.sla_unidade}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
